import { useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { AxiosError } from 'axios';

import { EToasterStatus, showToast } from 'utils/showToast';

interface IUseOfferFormSubmitProps {
  edit: boolean;
  dup: boolean;
  offerLabel: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addOffer: { mutate: (payload: any, options?: any) => void };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateOffer: { mutate: (payload: any, options?: any) => void };
  onSuccess?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatError?: (error: any) => string;
}

interface IUseOfferFormSubmitReturn {
  submitOffer: (payload: unknown, offerId?: string) => void;
}

/**
 * Wraps the shared add/update offer mutation pattern with consistent
 * toast messages and navigation on success.
 *
 * @example
 * const { submitOffer } = useOfferFormSubmit({
 *   edit, dup, offerLabel: 'Bundle', addOffer, updateOffer,
 *   onSuccess: () => setTaskStatus(ETaskId.ACTIVATE_FIRST_BUNDLE),
 *   formatError: (error) => errorResponse(error)
 * });
 * submitOffer(newOffer, offerId);
 */
export const useOfferFormSubmit = ({
  edit,
  dup,
  offerLabel,
  addOffer,
  updateOffer,
  onSuccess,
  formatError
}: IUseOfferFormSubmitProps): IUseOfferFormSubmitReturn => {
  const navigate = useNavigate();

  const submitOffer = useCallback(
    (payload: unknown, offerId?: string): void => {
      const handleSuccess = (): void => {
        showToast({
          message: `${offerLabel} ${edit && !dup ? 'edited' : 'added'} successfully`,
          status: EToasterStatus.SUCCESS
        });
        onSuccess?.();
        navigate('../');
      };

      const handleError = (error: unknown): void => {
        if (formatError) {
          if (error instanceof AxiosError) {
            showToast({
              message: formatError(error),
              status: EToasterStatus.ERROR
            });
          }
        } else {
          showToast({
            message: `Error ${edit && !dup ? 'editing' : 'adding'} ${offerLabel}`,
            status: EToasterStatus.ERROR
          });
        }
      };

      const callbacks = {
        onSuccess: handleSuccess,
        onError: handleError
      };

      if (edit && offerId && !dup) {
        updateOffer.mutate({ offerId, form: payload }, callbacks);
      } else {
        addOffer.mutate(payload, callbacks);
      }
    },
    [edit, dup, offerLabel, addOffer, updateOffer, navigate, onSuccess, formatError]
  );

  return { submitOffer };
};
